import React from 'react';
import { observer } from 'mobx-react';
import { action, computed } from 'mobx';
import { Link } from 'react-router-dom';

import { appStore } from '@app/AppStore.ts';
import { AzureAuth, UserPermissions } from '@app/Classes/AzureAuth';
import { applyDecorators, EmptyProps } from '@app/Helpers/Decorator';
import { devInfoStore } from "@app/Stores/DevInfoStore";
import { permissionStore } from '@app/Stores/PermissionStore';

import { ToolTipItem } from '@app/Components/TooltipItem/ToolTipItem';
import { modalService } from '@app/Components/Modal/Modal';
import ThemeSwitcher from "@app/Components/ThemeSwitcher";
import { Icon } from '@app/Components/Icon';
import './_header.scss';

const ImpersonationDialog = React.lazy(() => import('@app/Components/Dialogs/ImpersonationDialog'));

@observer
export default class Header extends React.Component {

    constructor(props: EmptyProps) {
        super(props);
        applyDecorators(this);
    }

    render() {
        return (
            <div className="header">
                <div className="header-content">
                    <div className="header-logo">
                        <Link to="/">
                            <div className="header-logo-brand" />
                        </Link>
                    </div>
                    <div className="header-main">
                        <div className="header-label">
                            <Link to="/">IRREGULATION HANDLING</Link>
                            <div className={`header-env ${devInfoStore.envClassName}`}>
                                <span className="app-env">--{devInfoStore.appEnvironment}--</span>
                                <span className="app-version">v{devInfoStore.appVersion}</span>
                            </div>
                        </div>
                        <div className="user-wrapper">
                            <ThemeSwitcher />
                            {this._renderImpersonation()}
                            <span className="user-name">{appStore.impersonatedUser?.name ? appStore.impersonatedUser.name : appStore.currentUserName}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private _renderImpersonation() {
        if (!AzureAuth.hasAccess([UserPermissions.FullPermissionImpersonate]) && !appStore.impersonatedUser) return null;

        if (appStore.impersonatedUser) {
            const impersonatedUserTooltipId = `impersonatedUser_tooltip`;
            const exitImpersonationTooltipId = `exitImpersonation_tooltip`;
            const exitImpersonateTooltip = `Exit from ${appStore.impersonatedUser.name ?? 'impersonated user'}`;
            return (
                <>
                    <Icon id={impersonatedUserTooltipId} name="user-secret" className="user-action-icon" onClick={this._openImpersonationTool} />
                    <Icon id={exitImpersonationTooltipId} name="eye-slash" className="user-action-icon" onClick={AzureAuth.exitImpersonation} />
                    <ToolTipItem targetId={impersonatedUserTooltipId} className="user-tooltip" text={this._tooltipRolesStr} placement="bottom" />
                    <ToolTipItem targetId={exitImpersonationTooltipId} className="user-tooltip" text={exitImpersonateTooltip} placement="bottom" />
                </>
            );
        }
        return <Icon name="hat-wizard" className="user-action-icon" title="Impersonation" onClick={this._openImpersonationTool} />;
    }

    @action.bound
    private async _openImpersonationTool() {
        await modalService.show(ImpersonationDialog);
    }

    @computed
    private get _tooltipRolesStr() {
        const realRoles = permissionStore.getRolesMatchFromEncodedRoles(appStore.realUserRoles);
        const impersonatedRoles = permissionStore.getRolesMatchFromEncodedRoles(appStore.impersonatedUserRoles);

        const realUserName = appStore.impersonatedUser?.principalId ? `Real name: ${appStore.currentUserName}.\n` : '';
        const realRolesString = realRoles ? `Real user: ${realRoles}.\n` : '';
        const impersonatedRolesString = appStore.impersonatedUser ? `Impersonated user: ${impersonatedRoles}.\n` : '';

        return `${realUserName}${realRolesString}${impersonatedRolesString}`;
    }
}
